import { create } from 'zustand';
import { PaginationType } from '../types/pagination';
import { useAlertStore } from './use-alert-store';
import {
  AdminType,
  deleteAdmin,
  fetchAdmins,
  FetchAdminsRequest,
  postAdmin,
  putAdmin,
  restoreAdmin,
} from './api/admins-api';

interface Store extends Partial<PaginationType> {
  fetchAdmins: (data: Partial<FetchAdminsRequest>) => Promise<void>;
  deleteAdmin: (id: string) => Promise<void>;
  restoreAdmin: (id: string) => Promise<void>;
  postAdmin: () => Promise<void>;
  putAdmin: () => Promise<void>;
  isLoadingFetch?: boolean;
  isLoading?: boolean;
  list?: AdminType[];

  admin?: Partial<AdminType>;
  handleUpdateAdmin: (data: Partial<AdminType>) => void;
  handleResetAdmin: () => void;
}

export const useAdminsStore = create<Store>((set, get) => ({
  handleUpdateAdmin: (data) => set({ admin: { ...get().admin, ...data } }),
  handleResetAdmin: () => set({ admin: undefined }),
  fetchAdmins: async (params) => {
    set({ isLoadingFetch: true });
    try {
      const { admins, ...other } = await fetchAdmins(params);

      set({ list: admins, ...other });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по администраторами', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  deleteAdmin: async (id) => {
    set({ isLoading: true });
    try {
      await deleteAdmin(id);
      useAlertStore.getState().add({ label: 'Администратор успешно удален', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: true } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось удалить администратора', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreAdmin: async (id) => {
    set({ isLoading: true });
    try {
      await restoreAdmin(id);
      useAlertStore
        .getState()
        .add({ label: 'Администратор успешно восстановлен', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: false } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось восстановить администратора', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  postAdmin: async () => {
    set({ isLoading: true });
    try {
      const admin = get().admin;
      if (!admin) {
        throw new Error('Admin is not defined');
      }
      const resp = await postAdmin(admin);
      const updated = [resp, ...(get().list || [])];
      set({ list: updated });
      useAlertStore.getState().add({ label: 'Администратор успешно создан', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось создать администратора', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  putAdmin: async () => {
    set({ isLoading: true });
    try {
      const admin = get().admin;
      if (!admin) {
        throw new Error('Admin is not defined');
      }
      await putAdmin(admin);
      const updated = get().list?.map((item) =>
        item.id === admin.id ? { ...item, ...admin } : item,
      );
      set({ list: updated });
      useAlertStore.getState().add({ label: 'Администратор успешно обновлен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить администратора', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
}));
